// ~/store/auth.ts
import { gbAttributes } from '~/plugins/05.featureFlags'

const initialState = {
	showModalSignUp: false,
	showModalLogin: false,
	showModalVerifyEmail: false,
	user: null,
	email_to_verify: null,
	token: null,
	isFetchingMe: false,
	type_modal_verify_email: null,
}

/**
 * Pinia store for authentication state.
 * Manages the JWT token, the current user object, and modal visibility for login/sign-up flows.
 * The token is persisted in a long-lived cookie and hydrated on every page load.
 */
export const useAuthStore = defineStore('auth', {
	state: () => ({ ...initialState }),
	getters: {
		loggedIn: (state) => Boolean(state.token),
	},
	actions: {
		/** Sets any top-level state property by key. */
		SET_STATE_AUTH({ type, value }: { type: string; value: any }) {
			;(this as any)[type] = value
		},
		/** Updates a single field on the current user object without replacing the whole object. */
		SET_USER_FIELD({ field, value }: { field: keyof typeof this.user; value: any }) {
			if (this.user) {
				;(this.user as any)[field] = value
			}
		},
		/** Stores the JWT in state and writes it to the auth cookie. */
		ADD_TOKEN(token: string) {
			const {
				public: { auth },
			} = useRuntimeConfig()

			this.token = token

			const authToken = useCookie(auth.name_cookie, {
				path: '/',
				domain: auth.name_cookie_domain,
				maxAge: 60 * 60 * 24 * 365,
				secure: true,
			})

			authToken.value = token
		},
		/** Clears the JWT and user from state, removes the auth cookie, and resets GrowthBook attributes. */
		REMOVE_TOKEN() {
			const { $feature } = useNuxtApp()
			const {
				public: { auth },
			} = useRuntimeConfig()

			this.user = null
			this.token = null
			const authToken = useCookie(auth.name_cookie, {
				path: '/',
				domain: auth.name_cookie_domain,
			})

			authToken.value = null

			// ✅ Limpiar cookie del banner al hacer logout
			const dismissedBanner = useCookie('results_scan_ticket_banner_dismissed', {
				path: '/',
			})
			dismissedBanner.value = null

			$feature.setAttributes({})
			$feature.refreshFeatures()
		},
		/** Calls the registration endpoint. Does not auto-login; the caller must handle the token. */
		async register(body: any) {
			const { $repository } = useNuxtApp()
			const response = await $repository.v1.auth.postRegister(body)
			return response
		},
		/** Authenticates with email/password. Does not store the token; use `ADD_TOKEN` after success. */
		async login(body: any) {
			const { $repository } = useNuxtApp()
			const response = await $repository.v1.auth.postLogin(body)
			return response
		},
		/** Exchanges a magic-link token for a session and stores the resulting JWT. */
		async loginWithToken(body: any) {
			const { $repository } = useNuxtApp()
			const response = await $repository.v1.auth.getLoginToken(body)
			if (response.success) {
				this.ADD_TOKEN(response.result.access_token)
				this.user = response.result?.user
			}
			return response
		},
		/** Exchanges an OTP code for a session and stores the resulting JWT. */
		async loginWithCode(body: any) {
			const { $repository } = useNuxtApp()
			const response = await $repository.v1.auth.getLoginCode(body)

			if (response.success) {
				this.ADD_TOKEN(response.result.access_token)
				this.user = response.result?.user
			}
			return response
		},
		/**
		 * Fetches the current user profile and stores it.
		 * Guards against concurrent calls via `isFetchingMe`.
		 * Also updates GrowthBook attributes on the client so feature flags reflect the user.
		 */
		async fetchMe() {
			if (this.isFetchingMe) return { success: false }
			this.isFetchingMe = true

			try {
				const { $repository, $feature } = useNuxtApp()

				if (!$repository?.v1) return { success: false }

				const response = await $repository.v1.user.getMe()

				if (response?.success) {
					this.user = response.result
					if (import.meta.client) {
						$feature?.setAttributes?.(gbAttributes(response.result))
						$feature?.refreshFeatures?.()
					}
					return response
				}

				return { success: false, ...response }
			} catch (e) {
				return { success: false, error: e }
			} finally {
				this.isFetchingMe = false
			}
		},
		/** Authenticates via Google OAuth2 access token and stores the resulting JWT. */
		async saveAuthGoogle(body: any) {
			const { $repository } = useNuxtApp()
			const response = await $repository.v1.auth.postAuthGoogle(body)
			if (response.success) {
				this.ADD_TOKEN(response.result.access_token)
			}
			return response
		},
		/** Activates an account via email-link token and stores the resulting session. */
		async activationUser(token: string) {
			const { $repository } = useNuxtApp()
			const response = await $repository.v1.auth.getActivationUser(token)
			if (response.success) {
				this.ADD_TOKEN(response.result.access_token)
				this.user = response.result?.user
			}
			return response
		},
		/** Activates an account via OTP activation code and stores the resulting session. */
		async activationUserWithCode(code: string) {
			const { $repository } = useNuxtApp()
			const response = await $repository.v1.auth.getActivationUserWithCode(code)

			if (response.success) {
				this.ADD_TOKEN(response.result.access_token)
				this.user = response.result?.user
			}
			return response
		},
		/** Resends the account-activation email. */
		async resendActivation(body: any) {
			const { $repository } = useNuxtApp()
			const response = await $repository.v1.auth.postResendActivation(body)
			return response
		},
	},
})
